import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, BehaviorSubject, combineLatest, forkJoin } from 'rxjs';
import { DataService } from '../data.service';
import { switchMap, shareReplay, map, tap, catchError, filter, delay } from 'rxjs/operators';
import { StateCountryService } from '../state-country/state-country.service';
import { NameAndAddressRecordType } from 'src/app/shared/common-components/AppConstants';
import { LoadingService } from '../loading/loading.service';
import { NameAndAddress, IssuingOfficeDetails, StateCountry } from 'src/app/shared/common-components/common-structure';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root'
})
export class NameAndAddressService extends DataService {

  private baseURI = this.apiUri + '/nameandaddress/';
  private issuingOfficeURI = this.publicApiUri + '/issuingoffice/';
  private shipmentId = new BehaviorSubject<number>(0);

  shipmentAddresses$ = this.shipmentId.pipe(filter(s => s > 0)).pipe(
    switchMap((shipmentId) => this.getShipmentAddresses(shipmentId))
  );
  allStates$ = this.stateCountryService.getAllStates().pipe(shareReplay(1));
  allCountriesList$ = this.stateCountryService.getAllCountries().pipe(shareReplay(1));
  user$ = this.userService.getCurrentUserObservable().pipe(shareReplay(1));

  shipmentAddressesWithDefaultIssuingOffice$ = combineLatest([this.shipmentId, this.user$]).pipe(
    filter(([shipmentId, user]) => shipmentId > 0 && user !== undefined),
    tap(t => this.loader.show()),
    switchMap(([shipmentId, user]) => forkJoin([this.getShipmentAddresses(shipmentId), this.getIssuingOfficeDetails(user)])),
    tap(t => this.loader.hide()),
    shareReplay(1)
  );

  allShipmentAddresses$ = combineLatest([this.shipmentAddressesWithDefaultIssuingOffice$, this.allCountriesList$, this.allStates$])
    .pipe(
      map(([addressesWithDefaultIssuingOffice, countries, states]) =>
        this.mapDefaultIssuingOfficeDetails(addressesWithDefaultIssuingOffice, countries, states)
      ), shareReplay(1));

  originShipmentAddresses$ = this.allShipmentAddresses$.pipe(map(addresses =>
    addresses.filter(a => (a.recordType === NameAndAddressRecordType.ORIGIN
      || a.recordType === NameAndAddressRecordType.BILL_TO
      || a.recordType === NameAndAddressRecordType.SHIPPER)))
  );

  destinationShipmentAddresses$ = this.allShipmentAddresses$.pipe(map(addresses =>
    addresses.filter(a => (a.recordType === NameAndAddressRecordType.DESTINATION
      || a.recordType === NameAndAddressRecordType.CONSIGNEE
      || a.recordType === NameAndAddressRecordType.ISSUE_OFFICE)))
  );


  constructor(http: HttpClient, private loader: LoadingService,
    private userService: UserService,
    private stateCountryService: StateCountryService) {
    super(http);
  }

  saveAddresses(nameandaddresses: NameAndAddress[]): Observable<any> {
    return this.http
      .put(this.baseURI + 'save', nameandaddresses);
  }

  getShipmentAddresses(shipmentsId: any): Observable<NameAndAddress[]> {
    this.setUri(this.baseURI + shipmentsId + '/shipmentsid');
    return this.get().pipe(catchError((e) => of([] as NameAndAddress[])));
  }

  getShipmentAddressesForAccessorials(shipmentsId: any): Observable<NameAndAddress[]> {
    this.setUri(this.baseURI + 'accessorials/' + shipmentsId + '/shipmentsid');
    return this.get().pipe(catchError((e) => of([] as NameAndAddress[])));
  }

  updateShipmentId(shipmentId: number) {
    this.shipmentId.next(shipmentId);
  }

  // Function to get issuing office details
  getIssuingOfficeDetails(user: any): Observable<IssuingOfficeDetails> {
    const getOfficeDetailsUrl = this.issuingOfficeURI + user.id + '/userid';
    this.setUri(getOfficeDetailsUrl);
    let params: HttpParams = new HttpParams();
    params = params.append('organizationId', user.organizationId);
    return this.get({params}).pipe(catchError((e) => of({} as IssuingOfficeDetails)));
  }

  private mapDefaultIssuingOfficeDetails(addressesWithDefaultIssuingOffice: [NameAndAddress[], IssuingOfficeDetails],
    countries: StateCountry[], states: StateCountry[]) {
    const addresses = addressesWithDefaultIssuingOffice[0] ? addressesWithDefaultIssuingOffice[0] : [] as NameAndAddress[];
    let issuingOffice = addresses.find(address => address.recordType === NameAndAddressRecordType.ISSUE_OFFICE);
    const defaultIssuingOffice = addressesWithDefaultIssuingOffice[1];
    if (!issuingOffice && defaultIssuingOffice) {
      issuingOffice = new NameAndAddress();
      issuingOffice.recordType = NameAndAddressRecordType.ISSUE_OFFICE;
      issuingOffice.firstName = defaultIssuingOffice.officerFirstName;
      issuingOffice.lastName = defaultIssuingOffice.officerLastName;
      issuingOffice.middleInitial = defaultIssuingOffice.officerMiddleInitial;
      issuingOffice.issuingOfficerTitle = defaultIssuingOffice.officerTitle;
      issuingOffice.issuingOfficeName = defaultIssuingOffice.issuingOfficeName;
      issuingOffice.phone = defaultIssuingOffice.phoneNumber;
      issuingOffice.phoneExt = defaultIssuingOffice.phoneExtension;
      addresses.push(issuingOffice);
    }
    Object.values(NameAndAddressRecordType).forEach(recordType => {
      let address = addresses.find(a => a.recordType === recordType);
      if (!address) {
        address = { recordType } as NameAndAddress;
        addresses.push(address);
      }
      if (!address.countryId && recordType !== NameAndAddressRecordType.ISSUE_OFFICE) {
        address.countryId = 326;
      }
      address['state'] = address.stateId && states.find(state => state.stateCountryId === address.stateId) ?
        states.find(state => state.stateCountryId === address.stateId).stateCountryName : '';
      address['country'] = address.countryId && countries.find(ctry => ctry.stateCountryId === address.countryId) ?
        countries.find(ctry => ctry.stateCountryId === address.countryId) : '';
      address['displayAddressType'] = recordType === NameAndAddressRecordType.ORIGIN ? 'Origin Address'
        : recordType === NameAndAddressRecordType.BILL_TO ? 'Bill To Address'
          : recordType === NameAndAddressRecordType.SHIPPER ? 'Shipper Address'
            : recordType === NameAndAddressRecordType.DESTINATION ? 'Destination Address'
              : recordType === NameAndAddressRecordType.CONSIGNEE ? 'Consignee Address'
                : 'Issuing Office Address';
    });
    return addresses;
  }

}
